import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/auth.service";

const PIN_STATUS_KEY = ["auth", "pinStatus"];

/**
 * usePinStatus()
 *
 * Wraps GET /auth/pin/status. Use this to decide whether to show
 * "Set up PIN" vs "Change PIN" UI, and whether the PIN is currently locked
 * (too many failed verify attempts).
 */
export function usePinStatus(options = {}) {
  return useQuery({
    queryKey: PIN_STATUS_KEY,
    queryFn: () => authService.getPinStatus(),
    ...options,
  });
}

/**
 * usePinMutations()
 *
 * Usage:
 *   const { setupPin, changePin, verifyPin, isVerifyingPin, verifyPinError }
 *     = usePinMutations();
 *
 *   setupPin({ current_password, pin }, { onSuccess: () => navigate("/home") });
 *   verifyPin({ pin, purpose: "transaction" }, { onSuccess: (data) => {
 *     if (data.verified) submitTransfer();
 *   }});
 */
export function usePinMutations() {
  const queryClient = useQueryClient();

  const invalidateStatus = () =>
    queryClient.invalidateQueries({ queryKey: PIN_STATUS_KEY });

  const setupPinMutation = useMutation({
    mutationFn: (payload) => authService.setupPin(payload),
    onSuccess: invalidateStatus,
  });

  const changePinMutation = useMutation({
    mutationFn: (payload) => authService.changePin(payload),
    onSuccess: invalidateStatus,
  });

  const verifyPinMutation = useMutation({
    mutationFn: (payload) => authService.verifyPin(payload),
    // On a wrong PIN the backend likely 422s or returns verified:false —
    // check both. A failed attempt can also flip locked/locked_until, so
    // refresh status either way.
    onSettled: invalidateStatus,
  });

  return {
    setupPin: setupPinMutation.mutate,
    setupPinAsync: setupPinMutation.mutateAsync,
    isSettingUpPin: setupPinMutation.isPending,
    setupPinError: setupPinMutation.error,

    changePin: changePinMutation.mutate,
    changePinAsync: changePinMutation.mutateAsync,
    isChangingPin: changePinMutation.isPending,
    changePinError: changePinMutation.error,

    verifyPin: verifyPinMutation.mutate,
    verifyPinAsync: verifyPinMutation.mutateAsync,
    isVerifyingPin: verifyPinMutation.isPending,
    verifyPinError: verifyPinMutation.error,
    verifyPinResult: verifyPinMutation.data, // { verified, purpose }
  };
}